/**
 * Which install route a visitor is on, and therefore what we can honestly offer them (F-121).
 *
 * Leo, 2026-09-16: *"add to Home Screen instructions for iOS means if you're not on safari go
 * to safari (I for one never use safari) then hit the three dots then share then more then add,
 * it's crazy terrible install procedure, I want a one click way."*
 *
 * **On iOS there is no one-click way, and no amount of work here will produce one.** WebKit
 * exposes no install API: no `beforeinstallprompt`, no `navigator.install`, nothing a page can
 * call. Apple reserves the decision for the Share sheet. Any site that appears to install
 * itself in one tap is either not on iOS or is showing instructions too, and building something
 * that *looks* like a one-tap install and then silently does nothing would be worse than the
 * prose we have — a button that does not work teaches people the app is broken.
 *
 * So this file does the two things that are actually available:
 *
 * - **Where a real install API exists, use it.** Chromium on Android and on the desktop fires
 *   `beforeinstallprompt`, which can be held and fired from a button. That genuinely is one
 *   tap, and we were not using it at all — Chrome's own address-bar prompt was the only way in.
 * - **Where it does not, shorten the path rather than describe it.** The iOS journey Leo
 *   describes is long mostly because of its *first* step: he is not in Safari, and the
 *   instructions begin by telling him to go there without helping him do it. A hand-off to
 *   Safari at the current URL removes that step and the "three dots then more" hunting with it.
 *
 * Pure and DOM-free so the routing can be tested, for `chat/keyboard.ts`'s reason: the last
 * platform-detection decision in this codebase to live inside a component shipped four wrong
 * fixes before anybody measured anything.
 */

/** What we can offer this visitor. */
export type InstallRoute =
  /** Already on the home screen. Offer nothing; there is nothing to add. */
  | 'installed'
  /** A real install API is waiting. One tap, and the browser does the rest. */
  | 'prompt'
  /** iOS in Safari: the Share sheet is two taps away and we can name them exactly. */
  | 'ios-safari'
  /** iOS in something else: hand off to Safari first, which is the step that hurts. */
  | 'ios-other'
  /** No install path worth describing. Say nothing rather than invent one. */
  | 'none';

/** What the decision needs to know, so a test can state it without a browser. */
export interface InstallFacts {
  /** Running from the home screen already. */
  standalone: boolean;
  /** iOS or iPadOS, including the iPad that reports itself as a Mac. */
  apple: boolean;
  /** Safari proper, rather than another browser wearing WebKit on iOS. */
  safari: boolean;
  /** A `beforeinstallprompt` has been captured and not yet spent. */
  deferred: boolean;
}

/**
 * The single decision, kept in one place because every branch of it is a different promise.
 *
 * Order matters and is not arbitrary. `standalone` wins over everything: an installed app that
 * offers to install itself is the clearest possible signal that nobody is paying attention.
 * `deferred` comes next, because a captured prompt is the only honest "one tap" on offer and
 * should be used wherever it exists — including on an Android device where a plausible-looking
 * instruction card would be strictly worse than the real thing.
 *
 * `none` covers desktop Firefox and Safari, where there is an install of sorts behind a menu
 * that differs by version. Saying nothing is deliberate: an instruction that is wrong by one
 * menu level sends somebody hunting, which is exactly the experience being complained about.
 */
export function installRoute(facts: InstallFacts): InstallRoute {
  if (facts.standalone) return 'installed';
  if (facts.deferred) return 'prompt';
  if (facts.apple) return facts.safari ? 'ios-safari' : 'ios-other';
  return 'none';
}

/**
 * Whether it is worth showing anything at all.
 *
 * Its own function because "no route" and "already installed" both mean *show nothing* while
 * meaning opposite things, and a caller writing `route !== 'installed'` would show the empty
 * card to every desktop Firefox visitor.
 */
export function offersInstall(route: InstallRoute): boolean {
  return route === 'prompt' || route === 'ios-safari' || route === 'ios-other';
}

/**
 * The URL that opens this page in Safari from another iOS browser.
 *
 * `x-safari-https://` is the scheme Safari registers for exactly this hand-off. It is not in
 * any documentation Apple publishes, which is why the caller must treat a navigation to it as
 * something that may simply not happen: if the browser does not recognise the scheme, nothing
 * occurs at all — no error, no navigation, no event. The caller shows the manual path after a
 * short wait rather than assuming either outcome.
 *
 * Only `https` is rewritten. A `http` page has no business being installed, and a `blob:` or
 * `data:` URL handed to a scheme handler is the kind of thing worth refusing outright.
 */
export function safariHandoff(href: string): string | undefined {
  return href.startsWith('https://') ? `x-safari-${href}` : undefined;
}

/**
 * Whether this is Safari rather than another browser wearing WebKit.
 *
 * Pure, and here rather than in `prompt.ts`, because it is a function of a string and the
 * tests run without a DOM. See `chat/keyboard.ts` for the same split and the same reason.
 *
 * User-agent sniffing, which is the wrong tool almost everywhere and the right one here: what
 * is being detected is which *app* the page is in, and no feature distinguishes them — every
 * browser on iOS is WebKit, so they share every capability except the one that matters, which
 * is whether "Add to Home Screen" is in the share sheet this app opens.
 *
 * Matched by exclusion because Chrome, Edge, Firefox and Opera on iOS all carry `Safari` in
 * their own user agents and add a token of their own. Testing for `Safari` alone reports every
 * one of them as Safari, which would send Leo the two-step instruction for a share sheet he
 * cannot reach — the exact wrong answer for the exact person who complained.
 */
export function isSafari(agent: string): boolean {
  if (!/Safari/i.test(agent)) return false;
  return !/(CriOS|FxiOS|EdgiOS|OPiOS|Chrome|Chromium|Android)/i.test(agent);
}
